import { useMemo, useRef } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import LeadAccordion from "./LeadAccordion";
import DocumentViewer from "./DocumentViewer";
import { downloadDocument } from "../Utility/apiHelper";
import { useSnackbar } from "notistack";
import { handleValidateCodevalue, MAX_TOTAL_SIZE } from "../Utility/constant";
import { useSelector } from "react-redux";
import { formatFileSize } from "../Utility/calculations";


const Attachments = ({
  title = "Attachments",
  existingAttachments = [],
  newAttachments = [],
  onAddFiles,
  onRemoveNewAttachment,
  handleRemoveOldAttachment,
  isViewMode
}) => {
  const fileInputRef = useRef(null);

  const { enqueueSnackbar } = useSnackbar();
  const internationalise = useSelector(state => state?.internationalise?.currentHashMapData);


const totalSize = useMemo(() => {
  return (
    existingAttachments.reduce((s, a) => s + (a.file_size ?? 0), 0) +
    newAttachments.reduce((s, f) => s + (f.size ?? 0), 0)
  );
}, [existingAttachments, newAttachments]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const incomingSize = files.reduce((s,f)=>s+f.size,0);
    if (totalSize + incomingSize > MAX_TOTAL_SIZE) {
      enqueueSnackbar(handleValidateCodevalue(internationalise,"TEMP_SIZE_WARNING"),{variant:"warning"});
      fileInputRef.current.value = null;
      return;
    }

    onAddFiles(files);
    fileInputRef.current.value = null;
  };

const handleDownloadDoc = async (documentId) => {
  await downloadDocument(documentId);
};
  return (
    <LeadAccordion
      defaultOpen
      label={`${title} (${formatFileSize(totalSize)} / 25 MB)`}
    >
      {/* Upload */}
      {!isViewMode &&
      <Box mb={2}>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileSelect}
        />
      </Box>
    }

      {/* New Attachments */}
      {newAttachments.length > 0 && (
        <Box mb={3}>
          <Typography fontSize={13}>New Attachments</Typography>

          {newAttachments.map((file, index) => (
            <Box key={index} display="flex" gap={1} alignItems="center">
              <Typography fontSize={12}>{file.name}</Typography>
              <Typography fontSize={11}>
                ({formatFileSize(file.size)})
              </Typography>
              <IconButton
                size="small"
                onClick={() => onRemoveNewAttachment(index)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
{existingAttachments.length > 0 && (
  <Box
    display="grid"
    gridTemplateColumns="repeat(auto-fill,minmax(140px,1fr))"
    gap={2}
  >
    {existingAttachments.map(att => (
      <Box
        key={att.id}
        sx={{
          position: "relative",
          aspectRatio: "1 / 1",
          p: 1,
          borderRadius: 1,
          overflow: "hidden",
          backgroundColor: "#f5f5f5",
          "&:hover .hover-actions": {
            opacity: 1
          }
        }}
      >
        {/* Hover Actions */}
        <Box
          className="hover-actions"
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            backgroundColor: "rgba(0,0,0,0.45)",
            opacity: 0,
            transition: "opacity 0.2s ease-in-out",
            zIndex: 2
          }}
        >
          <Tooltip title="Download">
            <IconButton
              sx={{ color: "#fff" }}
              onClick={() => handleDownloadDoc(att.document_id)}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>

          {!isViewMode &&
          <Tooltip title="Delete">
            <IconButton
              sx={{ color: "#fff" }}
              onClick={() => handleRemoveOldAttachment(att.document_id)}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
          }
        </Box>

        {/* Preview */}
        <Box
          sx={{
            height: "70%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            mb: 1
          }}
        >
          <DocumentViewer
            documentId={att.document_id}
            fileName={att.file_name}
          />
        </Box>

        {/* File Name */}
        <Typography
          fontSize={12}
          textAlign="center"
          noWrap
        >
          {att.file_name}
        </Typography>

        {/* File Size */}
        <Typography
          variant="caption"
          color="text.secondary"
          textAlign="center"
          display="block"
        >
          {formatFileSize(att.file_size)}
        </Typography>
      </Box>
    ))}
  </Box>
)}

    </LeadAccordion>
  );
};

export default Attachments;
